import { Component, inject, input, signal, effect, Output, EventEmitter, OnInit, untracked, OnDestroy } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { InventoryService, InventoryType } from '@servicios/inventory/inventory.service';
import { OnboardingService } from '@servicios/onboarding.service';
import { Column, FieldConfig } from '@sharedComponents/table-dynamic/interfaces';
import { Subscription, firstValueFrom } from 'rxjs';
import { ActionButton } from '@sharedComponents/table-dynamic/interfaces/actions-buttons.interface';
import { Roles, TechConfigModels } from '@modelos/interfaces/operation-result-types';
import { AuthenticationService } from '@servicios/authentication.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Validators } from '@angular/forms';
import { ConfiguracionesTecnicasService } from '@servicios/configuracionesTecnicas.service';
import { ButtonConfig } from '@sharedComponents/modal-template/modal-template.component';
import { PipeService } from '@sharedComponents/table-dynamic/pipes/pipe.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TableDynamicComponent } from '@sharedComponents/table-dynamic/table-dynamic.component';
import { ModalBasicComponent } from '@sharedComponents/modal-basic/modal-basic.component';
import { ItemFormComponent } from './item-form/item-form.component';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TabsModule } from 'primeng/tabs';
import { ChipModule } from 'primeng/chip';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ItemLabelComponent } from '../item-label/item-label.component';

declare var pdfMake: any;

@Component({
  selector: 'app-table-items',
  templateUrl: './table-items.component.html',
  styleUrls: ['./table-items.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableDynamicComponent,
    ModalBasicComponent,
    DrawerModule,
    ButtonModule,
    TagModule,
    TabsModule,
    ChipModule,
    SelectModule,
    DialogModule,
    DividerModule,
    TableModule,
    TooltipModule,
    InputTextModule,
    InputGroupModule,
    ConfirmDialogModule,
    ItemLabelComponent,
    RouterModule
  ]
})
export class TableItemsComponent implements OnInit, OnDestroy {
  private service = inject(InventoryService);
  private authService = inject(AuthenticationService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private pipeService = inject(PipeService);
  private onboardingService = inject(OnboardingService);
  private router = inject(Router);
  private techConfigService = inject(ConfiguracionesTecnicasService);
  private tourSubscription?: Subscription;

  get canManageInventory(): boolean {
    const roles = this.authService.CURRENT_USER_ROLES || [];
    return roles.includes(Roles.ADMIN) || roles.includes(Roles.INVENTORY_MANAGER);
  }

  get canViewPrices(): boolean {
    const roles = this.authService.CURRENT_USER_ROLES || [];
    return roles.includes(Roles.ADMIN) || roles.includes(Roles.INVENTORY_MANAGER) || roles.includes(Roles.SUPERVISOR) || roles.includes(Roles.DIRECTOR);
  }

  // Input from parent to trigger item creation with a specific type
  selectedCategory = input<string | null>(null);
  branchId = input<string | null>(null);
  // Incremented by parent when a quick movement completes — triggers a data reload
  refreshTrigger = input<number>(0);
  
  @Output() onNewClicked = new EventEmitter<void>();

  type: InventoryType = 'items';

  loading = signal<boolean>(false);
  data = signal<any[]>([]);
  totalRecords = signal<number>(0);
  first = signal<number>(0);
  pageSize = signal<number>(10);
  cols = signal<Column[]>([]);
  actionButtons = signal<ActionButton[]>([]);
  
  // For label printing
  printItem = signal<any | null>(null);

  showModal = signal(false);
  selectedItem = signal<any | null>(null);
  fieldConfig = signal<FieldConfig[]>([]);
  buttonsConfig = signal<ButtonConfig[]>([]);

  // Ordenamiento
  sortField = signal<string>('id');
  sortOrder = signal<number>(1);

  // Stats
  stats = signal<any>({ alertStock: 0, lowStock: 0, highStock: 0, expiredCount: 0, expiringSoonCount: 0, totalValue: [] });
  activeStatusFilter = signal<string | null>(null);

  // Equipment Detail (Keep for now if needed, or remove if fully separated)
  showEquipmentDetail = signal(false);
  selectedEquipmentId = signal<string | null>(null);
  
  tableTitle = signal<string>('Artículos');

  // Master Data Signals (Required for Filters and Quick Create)
  brands = signal<any[]>([]);
  suppliers = signal<any[]>([]);
  forms = signal<any[]>([]);
  regulations = signal<any[]>([]);
  units = signal<any[]>([]);
  locations = signal<any[]>([]);

  // Signals for quick create modal
  showQuickCreateModal = signal(false);
  quickCreateItem = signal<any | null>(null);
  quickCreateFieldConfig = signal<FieldConfig[]>([]);
  quickCreateButtonsConfig = signal<ButtonConfig[]>([]);
  quickCreateTitle = signal<string>('');
  quickCreateType = signal<string>('');
  quickCreateTargetField = signal<string>('');

  // Report Modal
  showReportModal = signal(false);
  selectedRegulationForReport = signal<any | null>(null);

  itemTypes = [
    { label: 'Producto', value: 'PRODUCT' },
    { label: 'Servicio', value: 'SERVICE' },
    { label: 'Consumible', value: 'CONSUMABLE' },
    { label: 'Estándar', value: 'STANDARD' },
    { label: 'Reactivo', value: 'REACTIVE' },
    { label: 'Repuesto', value: 'SPARE_PART' },
    { label: 'Ítem de Ensayo', value: 'TEST_ITEM' }
  ];

  constructor() {
    // Effect to handle category selection from parent
      effect(() => {
        const category = this.selectedCategory();
        untracked(() => {
          if (category) {
            this.tableTitle.set('Artículos');
            this.first.set(0); 
            this.selectItemType(category);
          }
        });
      });

    // Effect to reload data when parent signals a refresh or branch changes
    effect(() => {
      const trigger = this.refreshTrigger();
      const branch = this.branchId();
      untracked(() => {
        this.loadData();
        this.loadStats();
      });
    });
  }

  ngOnInit() {
    this.loadConfig(); // Initial config to render columns
    this.loadOptions(); // Load options and update filters
    this.loadStats();
    // Explicit initial load to ensure data is fetched even if table onLazyLoad timing is off
    this.loadData({ first: this.first(), rows: this.pageSize() });
  }

  translateItemType(type: string): string {
    return this.pipeService.transform(type, 'item_type');
  }

  ngOnDestroy() {
    if (this.tourSubscription) {
        this.tourSubscription.unsubscribe();
    }
  }

  async loadStats() {
    try {
      const stats = await this.service.getStatistics(this.type, this.branchId() || undefined);
      this.stats.set(stats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }

  async loadOptions() {
      try {
          const [brandsRes, suppliersRes, formsRes, regulationsRes, locationsRes, unitsRes] = await Promise.all([
              this.service.getAll('brand'),
              this.service.getAll('supplier'),
              this.service.getAll('form'),
              this.service.getAll('regulations'),
              this.service.getAll('location'),
              firstValueFrom(this.techConfigService.getAll(TechConfigModels.units).valueChanges)
          ]);
          
          this.brands.set(brandsRes.data.map((b: any) => ({ label: b.name, value: b.id })));
          this.suppliers.set(suppliersRes.data.map((s: any) => ({ label: s.name, value: s.id })));
          this.forms.set(formsRes.data.map((f: any) => ({ label: f.name, value: f.id })));
          this.regulations.set(regulationsRes.data.map((r: any) => ({ label: r.name, value: r.id })));
          this.locations.set(locationsRes.data || []);
          if (unitsRes?.data?.techConfigs) {
            this.units.set(unitsRes.data.techConfigs.map((u: any) => ({
              label: u.value || u.name,
              value: u.id
            })));
          }

          this.updateFilterOptions();
      } catch (error) {
          console.error("Error loading options", error);
      }
  }

  updateFilterOptions() {
    // Update the filter options in the existing columns
    const currentCols = this.cols();
    const updatedCols = currentCols.map(col => {
      if (col.field === 'brand.name' && col.filter) {
        return { ...col, filter: { ...col.filter, options: this.brands() } };
      }
      if (col.field === 'suppliers' && col.filter) {
        return { ...col, filter: { ...col.filter, options: this.suppliers() } };
      }
      if (col.field === 'form.name' && col.filter) {
        return { ...col, filter: { ...col.filter, options: this.forms() } };
      }
      if (col.field === 'controlledBy' && col.filter) {
        return { ...col, filter: { ...col.filter, options: this.regulations() } };
      }
      return col;
    });
    this.cols.set(updatedCols);
  }


  loadConfig() {
    let columns: Column[] = [
      { field: 'id', header: 'ID', visible: false },
      { field: 'v_', header: 'Version', visible: false },
      { 
        field: 'type', 
        header: 'Tipo', 
        visible: true, 
        filter: { 
          enable: true, 
          type: 'multiselect', 
          matchMode: 'in', 
          showMenu: true,
          options: this.itemTypes,
          optionLabel: 'label',
          optionValue: 'value'
        },
        pipe: 'item_type'
      },
      { field: 'name', header: 'Nombre', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
      { field: 'internal_code', header: 'Código', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
      { field: 'description', header: 'Descripción', visible: true, filter: { enable: true, type: 'text', matchMode: 'contains', showMenu: true } },
      { 
        field: 'brand.name', 
        header: 'Marca', 
        visible: true, 
        filter: { 
          enable: true, 
          type: 'multiselect', 
          matchMode: 'in', 
          showMenu: true,
          options: this.brands(),
          optionLabel: 'label',
          optionValue: 'value',
          safeKey: 'brandId'
        } 
      },
      { 
        field: 'suppliers', 
        header: 'Proveedores', 
        visible: true, 
        filter: { 
          enable: true, 
          type: 'multiselect', 
          matchMode: 'in', 
          showMenu: true,
          options: this.suppliers(),
          optionLabel: 'label',
          optionValue: 'value',
          safeKey: 'supplierId'
        },
        pipe: 'array_name'
      },
      { 
        field: 'form.name', 
        header: 'Presentación', 
        visible: true, 
        filter: { 
          enable: true, 
          type: 'multiselect', 
          matchMode: 'in', 
          showMenu: true,
          options: this.forms(),
          optionLabel: 'label',
          optionValue: 'value',
          safeKey: 'formId'
        } 
      },
      { 
        field: 'controlledBy', 
        header: 'Regulaciones', 
        visible: true, 
        filter: { 
          enable: true, 
          type: 'multiselect', 
          matchMode: 'in', 
          showMenu: true,
          options: this.regulations(),
          optionLabel: 'label',
          optionValue: 'value',
          safeKey: 'regulationId'
        },
        pipe: 'array_name'
      },
      { field: 'tags', header: 'Etiquetas', visible: true, pipe: 'join' },
      { field: 'stock_display', header: 'Stock (Disp/Total)', visible: true },
      { field: 'price', header: 'Precio', visible: this.canViewPrices, pipe: 'number' },
      { field: 'currency', header: 'Moneda', visible: this.canViewPrices }
    ];

    this.cols.set(columns);
    this.updateActionButtons();
  }

  updateActionButtons() {
    const buttons: ActionButton[] = [
      {
        icon: 'pi pi-eye',
        label: 'Ver Detalles',
        type: 'view',
        action: (event: any) => this.openItemHub(event, 'view')
      }
    ];

    if (this.canManageInventory) {
      buttons.push({
        icon: 'pi pi-pencil',
        label: 'Editar',
        type: 'edit',
        roles: [Roles.ADMIN, Roles.INVENTORY_MANAGER],
        action: (event: any) => this.openItemHub(event, 'edit')
      });
    }

    buttons.push({
      icon: 'pi pi-print',
      label: 'Imprimir Etiqueta',
      type: 'custom',
      action: (event: any) => this.printLabel(event)
    });

    if (this.canManageInventory) {
      buttons.push({
        icon: 'pi pi-trash',
        label: 'Eliminar',
        type: 'delete',
        roles: [Roles.ADMIN, Roles.INVENTORY_MANAGER],
        action: (event: any) => this.confirmDelete(event)
      });
    }

    this.actionButtons.set(buttons);
  }

  async loadData(event?: any) {
    try {
      this.loading.set(true);
      
      const first = event?.first !== undefined ? event.first : this.first();
      const rows = event?.rows !== undefined ? event.rows : this.pageSize();
      const sortField = event?.sortField || this.sortField();
      const sortOrder = event?.sortOrder !== undefined ? event.sortOrder : this.sortOrder();
      const search = event?.globalFilter || event?.filters?.global?.value || '';

      this.first.set(first);
      this.pageSize.set(rows);
      this.sortField.set(sortField);
      this.sortOrder.set(sortOrder);

      const res = await this.service.getAll(this.type, {
        page: Math.floor(first / rows) + 1 || 1,
        limit: rows,
        search: search,
        sortBy: sortField,
        sortOrder: sortOrder > 0 ? 'asc' : 'desc',
        status: this.activeStatusFilter() || undefined,
        itemType: this.selectedCategory() || undefined,
        filters: event?.filters || undefined,
        branchId: this.branchId() || undefined
      });
      
      const mappedData = (res.data || []).map((i: any) => {
        const available = (i.stock || 0) - (i.reserved_stock || 0) - (i.blocked_stock || 0);
        return {
          ...i,
          stock_display: `${available} / ${i.stock || 0}`
        };
      });
      
      this.data.set(mappedData);
      this.totalRecords.set(res.total);
      this.loading.set(false);
    } catch (error) {
      console.error('Error loading inventory data:', error);
      this.loading.set(false);
    }
  }

  openItemHub(item: any, mode: 'view' | 'edit' | 'create' = 'view') {
    const targetMode = (!this.canManageInventory && mode !== 'view') ? 'view' : mode;
    if (targetMode === 'create') {
      if (!this.canManageInventory) return;
      this.router.navigate(['/inventory/items/new']);
    } else {
      this.router.navigate(['/inventory/items', item.id], { queryParams: { mode: targetMode } });
    }
  }

  newItem() {
    if (!this.canManageInventory) return;
    this.router.navigate(['/inventory/items/new']);
  }

  selectItemType(type: string) {
    if (!this.canManageInventory) return;
    this.router.navigate(['/inventory/items/new'], { queryParams: { type } });
  }

  confirmDelete(item: any) {
    this.confirmationService.confirm({
      message: `¿Está seguro de que desea eliminar este registro?`,
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.delete(item)
    });
  }

  async delete(item: any) {
    try {
      this.loading.set(true);
      await this.service.delete(this.type, item.id, item.v_);
      this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Registro eliminado correctamente' });
      this.loadData();
    } catch (error) {
      console.error(error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el registro' });
      this.loading.set(false);
    }
  }

  confirmDeleteSelected(items: any[]) {
    this.confirmationService.confirm({
      message: `¿Está seguro de que desea eliminar ${items.length} registros seleccionados?`,
      header: 'Confirmar eliminación múltiple',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.deleteSelected(items)
    });
  }

  async deleteSelected(items: any[]) {
    try {
      this.loading.set(true);
      const itemsToDelete = items.map(i => ({ id: i.id, v_: i.v_ }));
      await this.service.deleteMany(this.type, itemsToDelete);
      this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Registros eliminados correctamente' });
      this.loadData();
    } catch (error) {
      console.error(error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron eliminar los registros' });
      this.loading.set(false);
    }
  }

  hideModal() {
    this.showModal.set(false);
    this.selectedItem.set(null);
  }

  async saveItem(payload: any) {
    try {
      this.loading.set(true);
      
      if (payload.id) {
        // Update
        const payloadToUpdate = { ...payload };
        
        // Remove prohibited fields
        const fieldsToRemove = ['createdAt', 'updatedAt', 'brand', 'form', 'suppliers', 'controlledBy'];
        fieldsToRemove.forEach(f => delete (payloadToUpdate as any)[f]);

        await this.service.update(this.type, payload.id, payloadToUpdate);
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Registro actualizado correctamente' });
      } else {
        // Create
        await this.service.create(this.type, payload);
        this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Registro creado correctamente' });
      }
      await this.loadData();
    } catch (error: any) {
      console.error(error);
      let detail = 'No se pudo guardar el artículo';
      if (Array.isArray(error?.error?.message)) {
        detail = error.error.message.join('. ');
      } else if (typeof error?.error?.message === 'string') {
        detail = error.error.message;
      } else if (error?.message) {
        detail = error.message;
      }
      this.messageService.add({ severity: 'error', summary: 'Error al Guardar', detail, life: 7000 });
    } finally {
      this.loading.set(false);
    }
  }

  openQuickCreate(type: string, targetField: string) {
    this.quickCreateType.set(type);
    this.quickCreateTargetField.set(targetField);
    this.quickCreateItem.set({});
    this.quickCreateTitle.set(`Agregar ${this.getQuickCreateLabel(type)}`);
    
    let config: FieldConfig[] = [
      { key: 'name', label: 'Nombre', type: 'text', validators: [Validators.required], visible: true },
      { key: type === 'supplier' ? 'internal_code' : 'code', label: 'Código', type: 'text', validators: [Validators.required], visible: true },
      { key: 'description', label: 'Descripción', type: 'textarea', validators: [Validators.required], visible: true }
    ];

    this.quickCreateFieldConfig.set(config);
    this.quickCreateButtonsConfig.set([
      { label: 'Cancelar', icon: 'pi pi-times', type: 'cancel', action: () => this.hideQuickCreateModal() },
      { label: 'Guardar', icon: 'pi pi-check', type: 'save', action: () => { } }
    ]);
    this.showQuickCreateModal.set(true);
  }

  getQuickCreateLabel(type: string): string {
    switch(type) {
      case 'brand': return 'Marca';
      case 'supplier': return 'Proveedor';
      case 'form': return 'Presentación';
      case 'regulations': return 'Regulación';
      default: return '';
    }
  }

  hideQuickCreateModal() {
    this.showQuickCreateModal.set(false);
    this.quickCreateItem.set(null);
  }

  async saveQuickCreate(data: any) {
    try {
      this.loading.set(true);
      const type = this.quickCreateType();
      
      const cleanPayload: any = {
        name: data.name,
        description: data.description || '',
      };

      if (type === 'supplier') {
        cleanPayload.internal_code = data.internal_code || data.code;
      } else {
        cleanPayload.code = data.code;
      }

      // Remove active logic

      if (type === 'regulations') {
        cleanPayload.controller_organization = data.controller_organization || '';
      }

      const res = await this.service.create(type as any, cleanPayload);
      this.messageService.add({ severity: 'success', summary: 'Éxito', detail: 'Registro creado correctamente' });
      
      await this.loadOptions();
      
      this.hideQuickCreateModal();
    } catch (error) {
      console.error(error);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo crear el registro' });
    } finally {
      this.loading.set(false);
    }
  }


  async downloadAlertItems(type: 'alert' | 'low' | 'high') {
      try {
          this.loading.set(true);
          const items = await this.service.getItemsByStatus(type);
          let title = '';
          switch(type) {
              case 'alert': title = 'Reporte de Alerta de Stock'; break;
              case 'low': title = 'Reporte de Stock Crítico'; break;
              case 'high': title = 'Reporte de Exceso de Stock'; break;
          }
          this.generatePdfReport(items, title);
      } catch (error) {
          console.error("Error downloading report", error);
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo descargar el reporte' });
      } finally {
          this.loading.set(false);
      }
  }

  generatePdfReport(items: any[], title: string) {
        const docDefinition: any = {
            content: [
                { text: title, style: 'header' },
                { text: `Fecha: ${new Date().toLocaleDateString()}`, style: 'subheader' },
                {
                    style: 'tableExample',
                    table: {
                        headerRows: 1,
                        widths: ['auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto'],
                        body: [
                            [
                                { text: 'Código', style: 'tableHeader' }, 
                                { text: 'Nombre', style: 'tableHeader' }, 
                                { text: 'Marca', style: 'tableHeader' }, 
                                { text: 'Presentación', style: 'tableHeader' },
                                { text: 'Stock', style: 'tableHeader' },
                                { text: 'Min', style: 'tableHeader' },
                                { text: 'Max', style: 'tableHeader' }
                            ],
                            ...items.map(item => [
                                item.internal_code,
                                item.name,
                                item.brand?.name || '',
                                item.form?.name || '',
                                { text: item.stock.toString(), color: item.stock <= item.min_stock ? 'red' : (item.stock <= item.alert_stock ? 'orange' : 'black') },
                                item.min_stock.toString(),
                                item.max_stock.toString()
                            ])
                        ]
                    }
                }
            ],
            styles: {
                header: {
                    fontSize: 18,
                    bold: true,
                    margin: [0, 0, 0, 10]
                },
                subheader: {
                    fontSize: 14,
                    bold: true,
                    margin: [0, 10, 0, 5]
                },
                tableExample: {
                    margin: [0, 5, 0, 15]
                },
                tableHeader: {
                  bold: true,
                  fontSize: 13,
                  color: 'black'
                }
            }
        };

        (pdfMake as any).createPdf(docDefinition).open();
  }

  async openEquipmentDetail(item: any) {
    this.selectedEquipmentId.set(item.id);
    try {
        this.loading.set(true);
        await this.service.ensureEquipmentDetails(item.id);
        this.showEquipmentDetail.set(true);
    } catch (error) {
        console.error("Error ensuring equipment details", error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los detalles técnicos' });
    } finally {
        this.loading.set(false);
    }
  }

  openReportModal() {
    this.selectedRegulationForReport.set(null);
    this.showReportModal.set(true);
  }

  async generateReport() {
    const regId = this.selectedRegulationForReport();
    if (!regId) return;

    try {
      this.loading.set(true);
      const items = await this.service.getItemsByRegulation(regId);
      const regName = this.regulations().find(r => r.value === regId)?.label || 'Regulacion';
      
      const docDefinition = {
        content: [
          { text: `Reporte de Stock - ${regName}`, style: 'header' },
          { text: `Fecha: ${new Date().toLocaleDateString()}`, margin: [0, 0, 0, 20] },
          {
            table: {
              headerRows: 1,
              widths: ['*', 'auto', 'auto', 'auto'],
              body: [
                ['Nombre', 'Código', 'Stock', 'Ubicación'],
                ...items.map(i => [i.name, i.internal_code, i.stock, i.location?.name || '-'])
              ]
            }
          }
        ],
        styles: {
          header: { fontSize: 18, bold: true, margin: [0, 0, 0, 10] }
        }
      };

      (pdfMake as any).createPdf(docDefinition).open();
      this.showReportModal.set(false);
    } catch (e) {
      console.error(e);
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'No se pudo generar el reporte' });
    } finally {
      this.loading.set(false);
    }
  }

  toggleStatusFilter(status: string) {
    if (this.activeStatusFilter() === status) {
        this.activeStatusFilter.set(null);
    } else {
        this.activeStatusFilter.set(status);
    }
    this.first.set(0); 
    this.loadData();
  }

  clearStatusFilter() {
    this.activeStatusFilter.set(null);
    this.first.set(0);
    this.loadData();
  }

  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      'alert': 'Alerta de Stock',
      'low': 'Stock Crítico',
      'high': 'Exceso de Stock',
      'expired': 'Artículos Vencidos',
      'expiring': 'Próximos Vencimientos'
    };
    return labels[status] || status;
  }

  async printLabel(item: any) {
    this.printItem.set(item);
    // Wait for the component to render and generate the QR
    setTimeout(() => {
        window.print();
        // We could clear the item after printing, but window.print is blocking in some browsers
        // so we just leave it for now or use an afterprint event
    }, 500);
  }
}
